import React, { useState, useEffect, useRef } from 'react';
import { database, firestore, functions } from '../firebaseConfig';
import { ref, get, update, remove } from 'firebase/database';
import { doc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { computeAndStoreSignals } from './leadSignalEngine';
import { startFlowForContact } from './warmupFlowUtils';
import useCreditBalance, { estimateBulkCost } from '../hooks/useCreditBalance';
import { getAllContactEmails } from '../shared/enums';
import './AiCleanerPopup.css';

const AiCleanerPopup = ({
  isOpen,
  bulkSelectedRows,
  bulkContactsData,
  userId,
  closeAiCleaner,
  onOpenAiManager,
  onOpenAutoSend,
  showAutoSendButton = false,
  warmupFlows = [],
  onFlowAssigned
}) => {
  const [step, setStep] = useState('initial'); // initial, guidelineSelection, processing, emailValidation, crmCheck, confirm, missingRules
  const [aiSettings, setAiSettings] = useState({});
  const [cleanupGuidelinesList, setCleanupGuidelinesList] = useState([]);
  const [selectedGuidelineId, setSelectedGuidelineId] = useState(null);
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedData, setProcessedData] = useState({
    toKeep: [],
    toRemove: [],
    toRename: []
  });
  const [openAiManagerAfter, setOpenAiManagerAfter] = useState(false);
  const [openAutoSendAfter, setOpenAutoSendAfter] = useState(false);
  const [assignFlowAfter, setAssignFlowAfter] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [selectedToKeep, setSelectedToKeep] = useState({});
  const [isConfirming, setIsConfirming] = useState(false); // Neuer State für den Bestätigungsvorgang
  
  // Neue States für E-Mail-Validierung
  const [emailValidationProgress, setEmailValidationProgress] = useState(0);
  const [emailValidationResults, setEmailValidationResults] = useState({
    validEmails: [],
    invalidEmails: [],
    totalProcessed: 0
  });
  
  // States für DM-Email-Validierung
  const [dmEmailValidationResults, setDmEmailValidationResults] = useState({
    validCount: 0,
    invalidCount: 0,
    skippedCount: 0,
    totalProcessed: 0
  });

  // States für CRM Domain Guard (Close CRM)
  const [closeIntegration, setCloseIntegration] = useState(null);
  const [crmCheckResults, setCrmCheckResults] = useState({
    checkedDomains: 0,
    totalDomains: 0,
    blockedContacts: 0,
    allowedContacts: 0,
    errorDomains: 0
  });
  
  // States für Section-Toggles
  const [isKeepSectionOpen, setIsKeepSectionOpen] = useState(false);
  const [isRenameSectionOpen, setIsRenameSectionOpen] = useState(false);
  const [isRemoveSectionOpen, setIsRemoveSectionOpen] = useState(false);
  const [creditWarning, setCreditWarning] = useState(null);
  const processingStartedRef = useRef(false);

  const { balance: creditBalance } = useCreditBalance(userId);

  const getPacingLabel = (pacing) => {
    switch (pacing) {
      case 'formal': return 'Formal';
      case 'casual': return 'Casual';
      default: return 'Neutral';
    }
  };

  // Funktion für dynamische Progress-Texte
  const getProgressText = (progress) => {
    if (progress <= 10) return "Getting started...";
    if (progress <= 25) return "Warming up...";
    if (progress <= 50) return "Making progress...";
    if (progress <= 75) return "Nearly there...";
    if (progress <= 95) return "Almost done...";
    return "Finishing up...";
  };

  // Funktion für E-Mail-Validierungs-Status
  const getEmailValidationText = () => {
    const { validEmails, invalidEmails, totalProcessed } = emailValidationResults;
    
    if (totalProcessed === 0) {
      return "Starting validation...";
    }
    
    if (invalidEmails.length === 0) {
      return `All ${totalProcessed} emails are valid so far`;
    }
    
    if (validEmails.length === 0) {
      return `${invalidEmails.length} emails failed validation`;
    }
    
    return `${validEmails.length} valid, ${invalidEmails.length} invalid`;
  };

  const renderReasonBullets = (reason) => {
    if (!reason) return null;
    const sentences = reason
      .split(/(?<=[.!?;])\s+|,\s+(?=[A-Z])|\.\s*(?=[A-Z])/)
      .map(s => s.replace(/^[-•]\s*/, '').trim())
      .filter(s => s.length > 8)
      .slice(0, 6);
    if (sentences.length <= 1) return <span>{reason}</span>;
    return (
      <ul className="ai-cleaner-reason-bullets">
        {sentences.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    );
  };

  const renderProContra = (contact) => {
    const { proReasons = [], contraReasons = [] } = contact;
    const hasStructured = proReasons.length > 0 || contraReasons.length > 0;
    if (!hasStructured) {
      return renderReasonBullets(contact.reason);
    }
    return (
      <div className="ai-cleaner-pro-contra">
        {proReasons.length > 0 && (
          <div className="ai-cleaner-pro-col">
            <div className="ai-cleaner-pro-contra-header pro">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Pro</span>
            </div>
            <ul>
              {proReasons.map((r, i) => (
                <li key={i}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {contraReasons.length > 0 && (
          <div className="ai-cleaner-contra-col">
            <div className="ai-cleaner-pro-contra-header contra">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              <span>Contra</span>
            </div>
            <ul>
              {contraReasons.map((r, i) => (
                <li key={i}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Normalisiert User-Input in eine saubere Liste, ODER gibt Freitext unverändert durch
  const normalizeGuidelineSection = (raw) => {
    if (!raw || !raw.trim()) return '';
    const text = raw.trim();

    const hasListMarkers = /^[-•*]\s/m.test(text);
    const hasCommas = text.includes(',');
    const hasSemicolons = text.includes(';');
    const hasMultipleLines = text.includes('\n');
    const isStructuredInput = hasListMarkers || hasCommas || hasSemicolons || hasMultipleLines;

    if (!isStructuredInput) return text;

    const items = text
      .split(/[\n;]+/)
      .flatMap(line => {
        const trimmed = line.trim();
        if (!trimmed) return [];
        const cleaned = trimmed.replace(/^[-•*]\s*/, '');
        if (cleaned.includes(',')) {
          return cleaned.split(',').map(s => s.trim()).filter(Boolean);
        }
        return [cleaned];
      })
      .filter(Boolean);
    return items.map(item => `- ${item}`).join('\n');
  };

  // Hilfsfunktion zum Zusammenführen von keepSection und removeSection zu einem Guidelines-String
  const combineGuidelineSections = (guideline) => {
    if (!guideline) return '';
    
    if (guideline.keepSection || guideline.removeSection) {
      let combined = '';
      
      if (guideline.keepSection) {
        combined += `KEEP section - Businesses to keep:\n\n${normalizeGuidelineSection(guideline.keepSection)}\n\n`;
      }
      
      if (guideline.removeSection) {
        combined += `REMOVE section - Businesses to exclude:\n\n${normalizeGuidelineSection(guideline.removeSection)}\n\n`;
      }
      
      return combined.trim();
    }
    
    if (guideline.content) {
      return guideline.content;
    }
    
    return '';
  };

  // Reset beim erneuten Öffnen
  useEffect(() => {
    if (isOpen) {
      processingStartedRef.current = false;
      setCreditWarning(null);
      setError(null);
      setProgress(0);
      setDmEmailValidationResults({ validCount: 0, invalidCount: 0, skippedCount: 0, totalProcessed: 0 });
    }
  }, [isOpen]);

  // Lade AI Settings und Cleanup Guidelines
  useEffect(() => {
    const loadSettingsAndGuidelines = async () => {
      try {
        // Lade AI Settings
        const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
        const aiSnapshot = await get(aiSettingsRef);
        
        if (aiSnapshot.exists()) {
          setAiSettings(aiSnapshot.val());
        }

        // Lade Close CRM Integration (für CRM Domain Guard)
        try {
          const closeRef = ref(database, `/World/${userId}/integrations/crm/close`);
          const closeSnap = await get(closeRef);
          if (closeSnap.exists()) setCloseIntegration(closeSnap.val());
        } catch (err) {
          console.warn('[AI-CLEANER] Failed to load Close integration (non-blocking):', err.message);
        }
        
        // Lade Multi Cleanup Guidelines
        const cleanupGuidelinesRef = ref(database, `/World/${userId}/settings/cleanupGuidelinesList`);
        const guidelinesSnapshot = await get(cleanupGuidelinesRef);
        
        if (guidelinesSnapshot.exists()) {
          const guidelinesList = guidelinesSnapshot.val();
          const guidelinesArray = Object.keys(guidelinesList).map(key => ({
            id: key,
            ...guidelinesList[key]
          })).sort((a, b) => b.createdAt - a.createdAt);
          
          setCleanupGuidelinesList(guidelinesArray);
          
          if (guidelinesArray.length === 0) {
            setStep('missingRules');
            setLoading(false);
            return;
          }
          
          // Wenn nur eine Guideline vorhanden ist, direkt verwenden
          if (guidelinesArray.length === 1) {
            const singleGuideline = guidelinesArray[0];
            setSelectedGuidelineId(singleGuideline.id);
            setSelectedGuideline(singleGuideline);
            setStep('initial');
          } else {
            // Mehrere Guidelines vorhanden - Auswahl anzeigen
            const defaultGuideline = guidelinesArray.find(g => g.isDefault) || guidelinesArray[0];
            setSelectedGuidelineId(defaultGuideline.id);
            setSelectedGuideline(defaultGuideline);
            setStep('guidelineSelection');
          }
        } else {
          // Fallback: Prüfe alte cleanupGuidelines
          const aiSettings = aiSnapshot.exists() ? aiSnapshot.val() : {};
          if (!aiSettings.cleanupGuidelines || aiSettings.cleanupGuidelines.trim() === '') {
            setStep('missingRules');
            setLoading(false);
            return;
          }
          
          // Erstelle temporäre Guideline aus den alten Settings
          const fallbackGuideline = {
            id: 'legacy',
            name: 'Legacy Guidelines',
            content: aiSettings.cleanupGuidelines,
            isDefault: true,
            createdAt: Date.now()
          };
          setCleanupGuidelinesList([fallbackGuideline]);
          setSelectedGuidelineId(fallbackGuideline.id);
          setSelectedGuideline(fallbackGuideline);
          setStep('initial');
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Fehler beim Laden der Einstellungen:", error);
        setError("Fehler beim Laden der Einstellungen");
        setLoading(false);
      }
    };
    
    loadSettingsAndGuidelines();
  }, [userId]);

  // Starte die Verarbeitung, wenn die Komponente geladen ist und eine Guideline ausgewählt wurde
  useEffect(() => {
    if (!loading && !error && step === 'initial' && bulkContactsData.length > 0 && selectedGuideline) {
      if (selectedGuideline.emailValidationOnly) {
        startEmailValidationOnly();
      } else {
        startProcessing();
      }
    }
  }, [loading, error, step, bulkContactsData, selectedGuideline]);

  // Handler für Guideline-Auswahl
  const handleGuidelineSelection = (guideline) => {
    setSelectedGuidelineId(guideline.id);
    setSelectedGuideline(guideline);
  };

  const handleStartWithSelectedGuideline = () => {
    if (selectedGuideline) {
      setStep('initial');
    }
  };

  // Einzelnen Kontakt analysieren (wird parallel aufgerufen)
  const analyzeOneContact = async (contact, guidelines, shouldCleanNames, isHyper, businessContext) => {
    if (!contact || (!contact.business_name && !contact.lead_description)) {
      return { category: 'toRemove', data: { ...contact, reason: "Insufficient data for analysis" } };
    }

    const payload = {
      businessName: contact.business_name || '',
      description: contact.lead_description || '',
      email: contact.lead_email || '',
      guidelines,
      hyperIntelligence: isHyper,
      website: contact.lead_website || '',
      instagramCategory: contact.lead_instagram_category || '',
      city: contact.lead_city || '',
      country: contact.lead_country || '',
      businessType: contact.lead_type || '',
      instagram: contact.lead_instagram || '',
      facebook: contact.lead_facebook || '',
      linkedin: contact.lead_linkedin || ''
    };

    if (businessContext) payload.businessContext = businessContext;

    if (contact.lead_score) payload.leadScore = contact.lead_score;
    if (contact.spy_confidence_score) payload.spyConfidence = contact.spy_confidence_score;
    if (contact.lead_decision_makers) {
      try {
        const dms = typeof contact.lead_decision_makers === 'string' ? JSON.parse(contact.lead_decision_makers) : contact.lead_decision_makers;
        if (Array.isArray(dms) && dms.length > 0) {
          payload.decisionMakers = dms.map(dm => [dm.name, dm.role].filter(Boolean).join(' — ')).join(', ');
        }
      } catch (e) { /* ignore parse errors */ }
    }

    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.cleanupContact(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      const proReasons = Array.isArray(result.proReasons) ? result.proReasons : [];
      const contraReasons = Array.isArray(result.contraReasons) ? result.contraReasons : [];

      if (result.shouldKeep) {
        if (result.analysisError) {
          return { category: 'toKeep', data: { ...contact, reason: result.reason || "Analysis failed - kept for safety", confidence: 0, pacing: result.pacing || "neutral", toneTags: result.toneTags || [], proReasons, contraReasons, analysisError: true } };
        } else if (shouldCleanNames && result.cleanedBusinessName && result.cleanedBusinessName !== contact.business_name) {
          return { category: 'toRename', data: { ...contact, original: contact.business_name, cleaned: result.cleanedBusinessName, reason: result.reason || "Matches target customer categories", confidence: result.confidence || 7, pacing: result.pacing || "neutral", toneTags: result.toneTags || [], proReasons, contraReasons } };
        } else {
          return { category: 'toKeep', data: { ...contact, reason: result.reason || "Matches target customer categories", confidence: result.confidence || 7, pacing: result.pacing || "neutral", toneTags: result.toneTags || [], proReasons, contraReasons } };
        }
      } else {
        return { category: 'toRemove', data: { ...contact, reason: result.reason || "Does not match criteria", confidence: result.confidence || 8, pacing: result.pacing || "neutral", toneTags: result.toneTags || [], proReasons, contraReasons } };
      }
    } catch (err) {
      console.error(`[AI-CLEANER] Failed for ${contact.id}:`, err.message);
      return { category: 'toKeep', data: contact, failed: true };
    }
  };

  // Email-Validation-Only Modus: überspringt AI-Analyse komplett
  const startEmailValidationOnly = async () => {
    if (processingStartedRef.current) return;
    processingStartedRef.current = true;

    const allContacts = [...bulkContactsData];
    const toKeep = allContacts.map(contact => ({ ...contact }));

    const newProcessedData = { toKeep, toRemove: [], toRename: [] };
    setProcessedData(newProcessedData);

    const initialSelectedToKeep = {};
    toKeep.forEach(contact => {
      initialSelectedToKeep[contact.id] = true;
    });
    setSelectedToKeep(initialSelectedToKeep);

    setStep('emailValidation');
    await startEmailValidation(initialSelectedToKeep, newProcessedData);
  };

  // Hauptfunktion zum Analysieren und Filtern der Kontakte
  const startProcessing = async () => {
    if (processingStartedRef.current) return;
    processingStartedRef.current = true;

    setProgress(0);
    setCreditWarning(null);
    setStep('processing');

    const creditCostPerContact = selectedGuideline?.hyperIntelligence ? 2 : 1;
    const totalCreditCost = bulkContactsData.length * creditCostPerContact;

    if (creditBalance !== null) {
      const { affordable } = estimateBulkCost(bulkContactsData.length, creditCostPerContact, creditBalance);
      if (affordable < bulkContactsData.length) {
        setCreditWarning({ available: creditBalance, affordable, total: bulkContactsData.length });
      }
    }

    const deductCreditsFn = httpsCallable(functions, 'deductCredits');
    try {
      await deductCreditsFn({ action: 'contact_clean', amount: totalCreditCost, worldId: userId, metadata: { contactCount: bulkContactsData.length, hyperIntelligence: creditCostPerContact > 1 } });
    } catch (creditError) {
      const msg = creditError?.message || '';
      if (msg.includes('Not enough credits') || creditError?.code === 'resource-exhausted') {
        setCreditWarning({ exhausted: true, available: 0, processed: 0, total: bulkContactsData.length });
        processingStartedRef.current = false;
        setStep('confirm');
        return;
      }
      console.warn('[AI-CLEANER] Credit deduction failed (non-blocking):', msg);
    }

    try {
      const guidelines = selectedGuideline
        ? combineGuidelineSections(selectedGuideline)
        : (aiSettings.cleanupGuidelines || '');
      const shouldCleanNames = selectedGuideline?.cleanNames !== false;
      const isHyper = selectedGuideline?.hyperIntelligence === true;
      const businessContext = selectedGuideline?.businessContext || '';

      const toKeep = [];
      const toRemove = [];
      const toRename = [];
      let completedCount = 0;
      let failedCount = 0;

      const CONCURRENCY = 5;
      const queue = [...bulkContactsData];
      const processNext = async () => {
        while (queue.length > 0) {
          const contact = queue.shift();
          const result = await analyzeOneContact(contact, guidelines, shouldCleanNames, isHyper, businessContext);

          if (result.failed) failedCount++;
          if (result.category === 'toKeep') toKeep.push(result.data);
          else if (result.category === 'toRename') toRename.push(result.data);
          else toRemove.push(result.data);

          completedCount++;
          setProgress(Math.round((completedCount / bulkContactsData.length) * 100));
        }
      };

      const workers = Array.from({ length: Math.min(CONCURRENCY, bulkContactsData.length) }, () => processNext());
      await Promise.all(workers);

      // Refund für fehlgeschlagene Kontakte
      if (failedCount > 0) {
        const refundAmount = failedCount * creditCostPerContact;
        try {
          const addCreditsFn = httpsCallable(functions, 'deductCredits');
          await addCreditsFn({ action: 'contact_clean_refund', amount: -refundAmount, worldId: userId, metadata: { failedCount, refund: true } });
        } catch (refundErr) {
          console.warn('[AI-CLEANER] Refund failed:', refundErr.message);
        }
      }

      console.log(`[AI-CLEANER] Done: ${toKeep.length} keep, ${toRename.length} rename, ${toRemove.length} remove, ${failedCount} failed (refunded)`);

      const newProcessedData = { toKeep, toRemove, toRename };
      setProcessedData(newProcessedData);

      const initialSelectedToKeep = {};
      [...toKeep, ...toRename].forEach(contact => {
        initialSelectedToKeep[contact.id] = true;
      });
      setSelectedToKeep(initialSelectedToKeep);

      if (selectedGuideline?.keepEmptyEmails === true) {
        await proceedAfterValidation(initialSelectedToKeep, newProcessedData);
      } else {
        setStep('emailValidation');
        startEmailValidation(initialSelectedToKeep, newProcessedData);
      }
    } catch (error) {
      console.error("[AI-CLEANER] Processing failed:", error);
      setError("Fehler bei der Verarbeitung der Kontakte");
      processingStartedRef.current = false;
      setStep('initial');
    }
  };

  // Funktion zum Umschalten der Auswahl für einen Kontakt
  const toggleContactSelection = (contactId) => {
    setSelectedToKeep(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  // Funktion zum Umschalten der Auswahl für alle Kontakte in einer Kategorie
  const toggleAllInCategory = (category, value) => {
    const newSelectedToKeep = { ...selectedToKeep };
    
    if (category === 'toKeep') {
      processedData.toKeep.forEach(contact => {
        newSelectedToKeep[contact.id] = value;
      });
    } else if (category === 'toRename') {
      processedData.toRename.forEach(contact => {
        newSelectedToKeep[contact.id] = value;
      });
    } else if (category === 'toRemove') {
      processedData.toRemove.forEach(contact => {
        newSelectedToKeep[contact.id] = value;
      });
    }
    
    setSelectedToKeep(newSelectedToKeep);
  };

  // Hilfsfunktion: Trennt ein lead_email Feld in saubere einzelne Adressen.
  // Akzeptiert kommagetrennte oder semikolongetrennte Listen (häufig bei multi-Kontakt-Imports).
  const splitEmailField = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return [];
    return rawValue
      .split(/[,;\s]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  };

  // Klassifiziert ein Validation-Ergebnis als "deliverable | inconclusive | invalid"
  // Wichtig: catch-all (ok_for_all) wird als deliverable behandelt — diese Domains
  // akzeptieren in der Praxis fast immer Mails. Der Sender flaggt sie ohnehin als Risiko.
  // inconclusive (antispam_system, smtp_protocol, unknown, service_error) heißt
  // NICHT "ungültig" — die API konnte schlicht nicht prüfen (kein Credit verbraucht).
  const classifyEmailResult = (result) => {
    const status = result?.status;
    if (status === 'ok' || result?.isValid === true) return 'deliverable';
    if (status === 'ok_for_all' || result?.isCatchAll === true) return 'deliverable';
    if (['unknown', 'antispam_system', 'smtp_protocol', 'service_error'].includes(status)) return 'inconclusive';
    return 'invalid';
  };

  // Einzelne Adresse über das Backend prüfen (liefert immer ein Objekt zurück).
  const verifyEmailAddress = async (address) => {
    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.validateVerifyEmail(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: address })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      if (!payload.success) throw new Error(payload.error || 'Validation failed');
      return { ...payload.result, email: address };
    } catch (error) {
      console.error(`[AI-CLEANER] Email validation failed for ${address}:`, error.message);
      return { email: address, isValid: false, isCatchAll: false, isInconclusive: true, status: 'service_error', reason: 'Validation service temporarily unavailable' };
    }
  };

  // Einzelnen Kontakt validieren — nimmt das gesamte lead_email Feld, splittet
  // ggf. in mehrere Adressen, und liefert das beste Ergebnis zurück.
  // Reihenfolge: deliverable > inconclusive > invalid.
  const validateOneEmail = async (emailData) => {
    const addresses = splitEmailField(emailData.email);

    if (addresses.length === 0) {
      return {
        email: emailData.email,
        contactId: emailData.id,
        isValid: false,
        isCatchAll: false,
        isInconclusive: false,
        status: 'invalid_syntax',
        reason: 'Input value lacks a valid email address syntax (no credits deducted)',
        classification: 'invalid'
      };
    }

    let bestResult = null;
    let bestRank = -1;
    const RANK = { deliverable: 2, inconclusive: 1, invalid: 0 };

    for (const address of addresses) {
      const r = await verifyEmailAddress(address);
      const classification = classifyEmailResult(r);
      const rank = RANK[classification];
      if (rank > bestRank) {
        bestRank = rank;
        bestResult = { ...r, classification };
        if (classification === 'deliverable') break; // Optimierung: kein weiteres Splitting nötig
      }
    }

    return { ...bestResult, contactId: emailData.id };
  };

  // Funktion zum Starten der E-Mail-Validierung
  const startEmailValidation = async (selectedToKeepParam = null, processedDataParam = null) => {
    setEmailValidationProgress(0);
    
    try {
      const currentSelectedToKeep = selectedToKeepParam || selectedToKeep;
      const currentProcessedData = processedDataParam || processedData;
      const contactsToValidate = [
        ...currentProcessedData.toKeep.filter(contact => currentSelectedToKeep[contact.id]),
        ...currentProcessedData.toRename.filter(contact => currentSelectedToKeep[contact.id]),
        ...currentProcessedData.toRemove.filter(contact => currentSelectedToKeep[contact.id])
      ];
      
      const contactsWithEmails = contactsToValidate.filter(contact => 
        contact.lead_email && 
        contact.lead_email.trim() !== '' && 
        contact.lead_email.includes('@')
      );
      
      if (contactsWithEmails.length === 0) {
        await proceedAfterValidation(currentSelectedToKeep, currentProcessedData);
        return;
      }
      
      const emailsToValidate = contactsWithEmails.map(contact => ({
        email: contact.lead_email,
        id: contact.id
      }));
      
      const validEmails = [];
      const invalidEmails = [];
      let completedCount = 0;

      const EMAIL_CONCURRENCY = 5;
      const emailQueue = [...emailsToValidate];
      const processNextEmail = async () => {
        while (emailQueue.length > 0) {
          const emailData = emailQueue.shift();
          const emailResult = await validateOneEmail(emailData);

          // Nur eindeutig "invalid" Ergebnisse aussortieren.
          // catch-all (deliverable) und inconclusive bleiben drin — der Sender
          // entscheidet beim tatsächlichen Versand nochmal (PRE-SEND check).
          if (emailResult.classification === 'invalid') {
            invalidEmails.push(emailResult);
          } else {
            validEmails.push(emailResult);
          }

          completedCount++;
          setEmailValidationProgress(Math.round((completedCount / emailsToValidate.length) * 100));
          setEmailValidationResults({ validEmails, invalidEmails, totalProcessed: completedCount });
        }
      };

      const emailWorkers = Array.from(
        { length: Math.min(EMAIL_CONCURRENCY, emailsToValidate.length) },
        () => processNextEmail()
      );
      await Promise.all(emailWorkers);

      setEmailValidationResults({ validEmails, invalidEmails, totalProcessed: emailsToValidate.length });
      
      // Aktualisiere selectedToKeep - entferne invalide E-Mails
      const updatedSelectedToKeep = { ...currentSelectedToKeep };
      invalidEmails.forEach(emailResult => {
        if (emailResult.contactId) {
          updatedSelectedToKeep[emailResult.contactId] = false;
        }
      });
      setSelectedToKeep(updatedSelectedToKeep);
      
      // Verschiebe Kontakte mit invaliden E-Mails in die Remove-Liste
      const updatedProcessedData = { ...currentProcessedData };
      const invalidContactIds = invalidEmails.map(e => e.contactId).filter(Boolean);
      
      // Verarbeite invalide Kontakte
      invalidEmails.forEach(emailResult => {
        if (emailResult.contactId) {
          // Finde den ursprünglichen Kontakt
          const originalContact = contactsToValidate.find(c => c.id === emailResult.contactId);
          if (originalContact) {
            // Entferne aus der ursprünglichen Liste
            updatedProcessedData.toKeep = updatedProcessedData.toKeep.filter(contact => contact.id !== emailResult.contactId);
            updatedProcessedData.toRename = updatedProcessedData.toRename.filter(contact => contact.id !== emailResult.contactId);
            
            // Prüfe, ob er bereits in der Remove-Liste ist
            const alreadyInRemove = updatedProcessedData.toRemove.some(contact => contact.id === emailResult.contactId);
            if (!alreadyInRemove) {
              updatedProcessedData.toRemove.push({
                ...originalContact,
                reason: `Invalid email: ${emailResult.reason}`,
                emailValidationFailed: true
              });
            }
          }
        }
      });
      
      setProcessedData(updatedProcessedData);
      
      if (selectedGuideline?.verifyDecisionMakerEmails === true) {
        const dmResult = await validateDmEmails(updatedSelectedToKeep, updatedProcessedData);
        // validateDmEmails may adjust selected/processed further; use returned state when available
        const afterSelected = dmResult?.selected || updatedSelectedToKeep;
        const afterProcessed = dmResult?.processed || updatedProcessedData;
        await proceedAfterValidation(afterSelected, afterProcessed);
      } else {
        setDmEmailValidationResults({ validCount: 0, invalidCount: 0, skippedCount: 0, totalProcessed: 0 });
        await proceedAfterValidation(updatedSelectedToKeep, updatedProcessedData);
      }

    } catch (error) {
      console.error("Fehler bei der E-Mail-Validierung:", error);
      setError("Fehler bei der E-Mail-Validierung: " + error.message);
      setStep('confirm');
    }
  };

  // DM-Email-Validierung: prüft unverifizierte DM-Emails
  const validateDmEmails = async (currentSelectedToKeep, currentProcessedData) => {
    const allKeptContacts = [
      ...currentProcessedData.toKeep.filter(c => currentSelectedToKeep[c.id]),
      ...currentProcessedData.toRename.filter(c => currentSelectedToKeep[c.id])
    ];

    const dmEmailsToValidate = [];
    let skippedCount = 0;

    for (const contact of allKeptContacts) {
      if (!contact.lead_decision_makers) continue;
      let dms;
      try {
        dms = typeof contact.lead_decision_makers === 'string'
          ? JSON.parse(contact.lead_decision_makers)
          : contact.lead_decision_makers;
      } catch { continue; }
      if (!Array.isArray(dms)) continue;

      for (let i = 0; i < dms.length; i++) {
        const dm = dms[i];
        if (!dm.email || !dm.email.includes('@')) continue;

        if (dm.emailChecked === true) {
          skippedCount++;
          continue;
        }

        dmEmailsToValidate.push({ email: dm.email, contactId: contact.id, dmIndex: i, dmName: dm.name });
      }
    }

    if (dmEmailsToValidate.length === 0) {
      setDmEmailValidationResults({ validCount: 0, invalidCount: 0, skippedCount, totalProcessed: 0 });
      return { selected: currentSelectedToKeep, processed: currentProcessedData };
    }

    let dmValidCount = 0;
    let dmInvalidCount = 0;
    let dmProcessed = 0;
    const invalidDmEntries = [];
    const validDmEntries = [];

    const DM_EMAIL_CONCURRENCY = 5;
    const dmQueue = [...dmEmailsToValidate];
    const processNextDmEmail = async () => {
      while (dmQueue.length > 0) {
        const entry = dmQueue.shift();
        const result = await validateOneEmail(entry);
        dmProcessed++;

        const isStrictlyValid = result.isValid === true && result.status === 'ok';
        const isCatchAll = result.isCatchAll === true || result.status === 'ok_for_all';
        const isServiceError = ['service_error', 'unknown', 'antispam_system', 'smtp_protocol'].includes(result.status);

        if (isStrictlyValid || isCatchAll) {
          dmValidCount++;
          validDmEntries.push(entry);
        } else if (isServiceError) {
          skippedCount++;
        } else {
          dmInvalidCount++;
          invalidDmEntries.push(entry);
        }

        setDmEmailValidationResults({
          validCount: dmValidCount,
          invalidCount: dmInvalidCount,
          skippedCount,
          totalProcessed: dmProcessed
        });
      }
    };

    const dmWorkers = Array.from(
      { length: Math.min(DM_EMAIL_CONCURRENCY, dmEmailsToValidate.length) },
      () => processNextDmEmail()
    );
    await Promise.all(dmWorkers);

    // Markiere validierte DM-Emails + entferne invalide DM-Emails
    let finalProcessedData = currentProcessedData;
    if (invalidDmEntries.length > 0 || validDmEntries.length > 0) {
      const updatedData = { ...currentProcessedData };
      const updateContactDms = (contactList) => {
        return contactList.map(contact => {
          const invalidForContact = invalidDmEntries.filter(e => e.contactId === contact.id);
          const validForContact = validDmEntries.filter(e => e.contactId === contact.id);
          if (invalidForContact.length === 0 && validForContact.length === 0) return contact;

          let dms;
          try {
            dms = typeof contact.lead_decision_makers === 'string'
              ? JSON.parse(contact.lead_decision_makers)
              : [...contact.lead_decision_makers];
          } catch { return contact; }

          const invalidIndices = new Set(invalidForContact.map(e => e.dmIndex));
          const validIndices = new Set(validForContact.map(e => e.dmIndex));
          const cleanedDms = dms.map((dm, idx) => {
            if (invalidIndices.has(idx)) {
              return {
                ...dm,
                email: null,
                emailValidationFailed: true,
                emailSearched: true,
                emailChecked: true,
                emailValidationStatus: 'invalid',
              };
            }
            if (validIndices.has(idx)) {
              return {
                ...dm,
                emailChecked: true,
                emailValidationFailed: false,
                emailVerified: true,
                emailValidationStatus: 'valid',
              };
            }
            return dm;
          });

          return { ...contact, lead_decision_makers: JSON.stringify(cleanedDms) };
        });
      };

      updatedData.toKeep = updateContactDms(updatedData.toKeep);
      updatedData.toRename = updateContactDms(updatedData.toRename);
      setProcessedData(updatedData);
      finalProcessedData = updatedData;
    }

    setDmEmailValidationResults({
      validCount: dmValidCount,
      invalidCount: dmInvalidCount,
      skippedCount,
      totalProcessed: dmEmailsToValidate.length
    });

    return { selected: currentSelectedToKeep, processed: finalProcessedData };
  };

  // ---------- CRM Domain Guard (Close CRM) ----------

  const shouldRunCrmCheck = () => {
    if (!closeIntegration) return false;
    if (!closeIntegration.connected) return false;
    if (closeIntegration.enabled === false) return false;
    // apiKey liegt nach DSGVO-Umstellung nur noch im Server-Secret-Pfad.
    // Client-seitiger Proxy für "Key hinterlegt" ist das maskierte Preview.
    if (!closeIntegration.apiKeyPreview) return false;
    if (!closeIntegration.leadSourceFieldId) return false;
    if (selectedGuideline && selectedGuideline.crmCheckEnabled === false) return false;
    return true;
  };

  const extractDomainFromEmail = (email) => {
    if (!email || typeof email !== 'string') return null;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) return null;
    const parts = trimmed.split('@');
    let dom = parts[parts.length - 1];
    dom = dom.replace(/^www\./, '').split('/')[0];
    if (!dom || !dom.includes('.')) return null;
    return dom;
  };

  const startCrmCheck = async (selectedToKeepParam, processedDataParam) => {
    try {
      const currentSelected = selectedToKeepParam || selectedToKeep;
      const currentData = processedDataParam || processedData;

      const activeContacts = [
        ...currentData.toKeep.filter((c) => currentSelected[c.id]),
        ...currentData.toRename.filter((c) => currentSelected[c.id]),
      ];

      const contactDomainMap = {};
      const domainSet = new Set();
      activeContacts.forEach((contact) => {
        const dom = extractDomainFromEmail(contact.lead_email);
        if (dom) {
          contactDomainMap[contact.id] = dom;
          domainSet.add(dom);
        }
      });

      const uniqueDomains = Array.from(domainSet);
      setCrmCheckResults({
        checkedDomains: 0,
        totalDomains: uniqueDomains.length,
        blockedContacts: 0,
        allowedContacts: 0,
        errorDomains: 0,
      });

      if (uniqueDomains.length === 0) {
        setStep('confirm');
        return;
      }

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.crmDomainCheck(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          domains: uniqueDomains,
          contactDomainMap,
          provider: 'close',
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload.success) {
        console.warn('[AI-CLEANER] CRM domain check failed, continuing without block:', payload.error);
        setCrmCheckResults((prev) => ({
          ...prev,
          checkedDomains: uniqueDomains.length,
          errorDomains: uniqueDomains.length,
          allowedContacts: Object.keys(contactDomainMap).length,
        }));
        setStep('confirm');
        return;
      }

      const results = payload.results || {};
      let blockedCount = 0;
      let allowedCount = 0;
      let errorCount = 0;

      const updatedSelected = { ...currentSelected };
      const updatedProcessed = {
        toKeep: [...currentData.toKeep],
        toRename: [...currentData.toRename],
        toRemove: [...currentData.toRemove],
      };

      Object.entries(contactDomainMap).forEach(([contactId, domain]) => {
        const res = results[domain];
        if (!res || res.status === 'check_error') {
          errorCount++;
          allowedCount++;
          return;
        }

        const status = res.status;
        const isBlocked = status === 'crm_found_other_source' || status === 'crm_found_empty_source';

        if (!isBlocked) {
          allowedCount++;
          return;
        }

        blockedCount++;
        updatedSelected[contactId] = false;

        const sourceLabel = status === 'crm_found_empty_source'
          ? 'empty Lead Source'
          : `Lead Source "${res.leadSourceValue || 'other'}"`;
        const reason = `Already in Close CRM (${sourceLabel}) — not ${closeIntegration?.whitelistValue || 'SalesGod'}`;

        updatedProcessed.toKeep = updatedProcessed.toKeep.filter((c) => c.id !== contactId);
        updatedProcessed.toRename = updatedProcessed.toRename.filter((c) => c.id !== contactId);

        const alreadyInRemove = updatedProcessed.toRemove.some((c) => c.id === contactId);
        if (!alreadyInRemove) {
          const original = activeContacts.find((c) => c.id === contactId);
          if (original) {
            updatedProcessed.toRemove.push({
              ...original,
              reason,
              crmBlocked: true,
              crmBlockStatus: status,
              crmBlockLeadId: res.leadId || null,
              crmBlockLeadSource: res.leadSourceValue || null,
              crmBlockDomain: domain,
            });
          }
        }
      });

      setCrmCheckResults({
        checkedDomains: uniqueDomains.length,
        totalDomains: uniqueDomains.length,
        blockedContacts: blockedCount,
        allowedContacts: allowedCount,
        errorDomains: errorCount,
      });

      setSelectedToKeep(updatedSelected);
      setProcessedData(updatedProcessed);

      if (blockedCount > 0) {
        setIsRemoveSectionOpen(true);
      }
      setStep('confirm');
    } catch (err) {
      console.error('[AI-CLEANER] CRM check error (non-blocking):', err);
      setStep('confirm');
    }
  };

  const proceedAfterValidation = async (currentSelected, currentProcessed) => {
    if (shouldRunCrmCheck()) {
      setStep('crmCheck');
      await startCrmCheck(currentSelected, currentProcessed);
    } else {
      setStep('confirm');
    }
  };

  // Funktion zum Bestätigen und Anwenden der Änderungen
  const confirmChanges = async () => {
    setIsConfirming(true);
    
    try {
      // Sammle alle Operationen zuerst, dann in 499er-Chunks committen
      const operations = [];
      const contactsToDelete = [];
      
      const cleanedTimestamp = new Date().toISOString();

      processedData.toRename.forEach(contact => {
        if (selectedToKeep[contact.id]) {
          const descAlreadyHasOriginal = (contact.lead_description || '').includes('Original Business Name:');
          const updatedDescription = descAlreadyHasOriginal
            ? contact.lead_description
            : (contact.lead_description 
                ? `${contact.lead_description}\n\nOriginal Business Name: ${contact.original}`
                : `Original Business Name: ${contact.original}`);
          
          const renameUpdate = {
            business_name: contact.cleaned,
            lead_description: updatedDescription,
            lead_pacing: contact.pacing || 'neutral',
            hasCleaned: true,
            cleanedAt: cleanedTimestamp
          };
          if (Array.isArray(contact.toneTags) && contact.toneTags.length >= 2) {
            renameUpdate.lead_tone_tags = contact.toneTags;
          }
          if (contact.lead_decision_makers) {
            renameUpdate.lead_decision_makers = typeof contact.lead_decision_makers === 'string'
              ? contact.lead_decision_makers
              : JSON.stringify(contact.lead_decision_makers);
          }
          operations.push({ type: 'update', id: contact.id, data: renameUpdate });
        } else {
          contactsToDelete.push(contact.id);
        }
      });
      
      processedData.toKeep.forEach(contact => {
        if (!selectedToKeep[contact.id]) {
          contactsToDelete.push(contact.id);
        } else {
          const keepUpdate = { hasCleaned: true, cleanedAt: cleanedTimestamp };
          if (contact.pacing) keepUpdate.lead_pacing = contact.pacing;
          if (Array.isArray(contact.toneTags) && contact.toneTags.length >= 2) {
            keepUpdate.lead_tone_tags = contact.toneTags;
          }
          if (contact.lead_decision_makers) {
            keepUpdate.lead_decision_makers = typeof contact.lead_decision_makers === 'string'
              ? contact.lead_decision_makers
              : JSON.stringify(contact.lead_decision_makers);
          }
          operations.push({ type: 'update', id: contact.id, data: keepUpdate });
        }
      });
      
      processedData.toRemove.forEach(contact => {
        if (!selectedToKeep[contact.id]) {
          contactsToDelete.push(contact.id);
        } else {
          operations.push({ type: 'update', id: contact.id, data: { hasCleaned: true, cleanedAt: cleanedTimestamp } });
        }
      });
      
      contactsToDelete.forEach(contactId => {
        operations.push({ type: 'delete', id: contactId });
      });

      // Firestore Batch-Limit: 500 ops UND 10 MiB Daten pro Batch.
      // Bei vielen Kontakten mit lead_decision_makers JSON kann 499 die 10 MiB Grenze sprengen.
      const BATCH_LIMIT = 149;
      for (let i = 0; i < operations.length; i += BATCH_LIMIT) {
        const chunk = operations.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(firestore);
        chunk.forEach(op => {
          const docRef = doc(firestore, `World/${userId}/db/${op.id}`);
          if (op.type === 'update') batch.update(docRef, op.data);
          else batch.delete(docRef);
        });
        await batch.commit();
      }

      const updateOps = operations.filter(op => op.type === 'update');
      for (const op of updateOps) {
        try {
          const originalContact = [...(processedData.toRename || []), ...(processedData.toKeep || []), ...(processedData.toRemove || [])]
            .find(c => c.id === op.id);
          if (originalContact) {
            const freshData = { ...originalContact, ...op.data };
            computeAndStoreSignals(userId, op.id, freshData, { force: true }).catch(() => {});
          }
        } catch (_) {}
      }

      const finalResults = {
        renamed: processedData.toRename.filter(contact => selectedToKeep[contact.id]).map(contact => {
          const descAlreadyHasOriginal = (contact.lead_description || '').includes('Original Business Name:');
          return {
            id: contact.id,
            original: contact.original,
            cleaned: contact.cleaned,
            lead_description: descAlreadyHasOriginal
              ? contact.lead_description
              : (contact.lead_description ? `${contact.lead_description}\n\nOriginal Business Name: ${contact.original}` : `Original Business Name: ${contact.original}`)
          };
        }),
        kept: processedData.toKeep.filter(contact => selectedToKeep[contact.id]),
        removed: contactsToDelete.length
      };
      
      const contactsToKeep = [
        ...finalResults.kept,
        ...processedData.toRename.filter(contact => selectedToKeep[contact.id]).map(contact => ({
          ...contact,
          business_name: contact.cleaned,
        })),
        ...processedData.toRemove.filter(contact => selectedToKeep[contact.id])
      ];
      
      // Flow-Zuweisung VOR dem Schließen, damit der Spinner sichtbar bleibt
      // und alle Firestore-Writes abgeschlossen sind bevor onReload() triggert
      if (assignFlowAfter && selectedFlowId && contactsToKeep.length > 0) {
        const flow = warmupFlows.find(f => f.id === selectedFlowId);
        if (flow) {
          let successCount = 0;
          let skipCount = 0;

          const contactIdsForFlow = [];
          for (const c of contactsToKeep) {
            if (c.warmupFlow) { skipCount++; }
            else { contactIdsForFlow.push(c.id); }
          }

          const FLOW_CONCURRENCY = 10;
          for (let i = 0; i < contactIdsForFlow.length; i += FLOW_CONCURRENCY) {
            const chunk = contactIdsForFlow.slice(i, i + FLOW_CONCURRENCY);
            const results = await Promise.all(
              chunk.map(id => startFlowForContact(userId, id, flow, 'warmup'))
            );
            successCount += results.filter(Boolean).length;
          }

          const allContactIds = contactsToKeep.map(c => c.id);

          closeAiCleaner(finalResults);
          if (onFlowAssigned) {
            onFlowAssigned(allContactIds, flow, successCount, skipCount);
          }
          return;
        }
      }
      
      closeAiCleaner(finalResults);
      
      const contactsWithValidEmail = contactsToKeep.filter(contact => {
        // Include decision-maker emails, not just the primary lead_email, so the
        // handoff to AI Manager / Auto Send keeps DM-only contacts.
        return getAllContactEmails(contact).some(e => e.email && e.email.trim() !== "" && !e.emailMissing);
      });
      
      const contactIdsToKeep = contactsWithValidEmail.map(contact => contact.id);
      
      if (openAiManagerAfter && contactsWithValidEmail.length > 0) {
        if (onOpenAiManager) {
          onOpenAiManager(contactIdsToKeep, contactsWithValidEmail);
        }
      } else if (openAutoSendAfter && contactsWithValidEmail.length > 0) {
        if (onOpenAutoSend) {
          onOpenAutoSend(contactIdsToKeep, contactsWithValidEmail);
        }
      }
    } catch (error) {
      console.error("Fehler beim Anwenden der Änderungen:", error);
      setError("Fehler beim Anwenden der Änderungen");
      setIsConfirming(false);
    }
  };

  // Funktion zum Öffnen der Einstellungsseite
  const navigateToSettings = () => {
    // Schließe den Cleaner
    closeAiCleaner();
    
    // Löse ein benutzerdefiniertes Event aus, um das AI-Tab in den Einstellungen zu aktivieren
    const settingsAiEvent = new CustomEvent('openSettingsTab', { 
      detail: { tab: 'ai' } 
    });
    document.dispatchEvent(settingsAiEvent);
    
    // Direkt zur WorldSettings-Komponente navigieren
    window.location.href = '/world/settings';
  };

  // Wenn das Popup nicht geöffnet ist, zeige nichts an
  if (!isOpen) return null;

  return (
    <div className="ai-cleaner-popup-overlay">
      <div className="ai-cleaner-popup-container">
        <div className="ai-cleaner-popup-header">
          <h2><span className="salesgod-bold">SalesGod</span> <span className="cleaner-normal">{selectedGuideline?.emailValidationOnly ? 'Email Check' : 'Cleaner'}</span> {selectedGuideline?.emailValidationOnly ? <span className="ai-badge email-only">Email Only</span> : selectedGuideline?.hyperIntelligence ? <span className="ai-badge hyper">Hyper Intelligence</span> : <span className="ai-badge">AI</span>}</h2>
          <button className="ai-cleaner-close-btn" onClick={closeAiCleaner}>×</button>
        </div>
        
        <div className="ai-cleaner-popup-content">
          {step === 'initial' && (
            <div className="ai-cleaner-loading">
              <div className="ai-cleaner-spinner"></div>
              <p>{loading ? 'Loading settings...' : 'Preparing...'}</p>
            </div>
          )}
          
          {error && (
            <div className="ai-cleaner-error">
              <p>{error}</p>
              <button onClick={closeAiCleaner}>Close</button>
            </div>
          )}
          
          {step === 'missingRules' && (
            <div className="ai-cleaner-missing-rules">
              <div className="ai-cleaner-icon-warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h3>Cleanup Rules Not Configured</h3>
              <p>
                Please define the rules for contact cleanup in the settings first.
                Without these rules, the AI Cleaner cannot work effectively.
              </p>
              <div className="ai-cleaner-buttons">
                <button 
                  className="ai-cleaner-cancel-btn" 
                  onClick={closeAiCleaner}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'guidelineSelection' && (
            <div className="ai-cleaner-guideline-selection">
              <div className="ai-cleaner-icon-selection">
                <img src="/img/wand.svg" style={{width: '30px', height: '30px'}} alt="AI Cleaning" />
              </div>
              <h3>Choose Cleanup Guidelines</h3>
              <p>
                Multiple cleanup guidelines are available. Please select which one to use for cleaning {bulkContactsData.length} contacts.
              </p>
              
              <div className="ai-guidelines-list">
                {cleanupGuidelinesList.map((guideline) => (
                  <div 
                    key={guideline.id}
                    className={`ai-guideline-option ${selectedGuidelineId === guideline.id ? 'selected' : ''}`}
                    onClick={() => handleGuidelineSelection(guideline)}
                  >
                    <div className="ai-guideline-radio">
                      <input
                        type="radio"
                        name="guideline"
                        checked={selectedGuidelineId === guideline.id}
                        onChange={() => handleGuidelineSelection(guideline)}
                      />
                    </div>
                    <div className="ai-guideline-info">
                      <div className="ai-guideline-name">
                        {guideline.name}
                        {guideline.isDefault && (
                          <span className="ai-guideline-default-badge">Default</span>
                        )}
                        {guideline.hyperIntelligence && (
                          <span className="ai-guideline-hyper-badge">Hyper</span>
                        )}
                        {guideline.emailValidationOnly && (
                          <span className="ai-guideline-email-only-badge">Email Only</span>
                        )}
                      </div>
                      <div className="ai-guideline-meta">
                        <span className="ai-guideline-rules">
                          {(() => {
                            const keepRules = guideline.keepSection ? guideline.keepSection.split('\n').filter(line => line.trim().startsWith('-')).length : 0;
                            const removeRules = guideline.removeSection ? guideline.removeSection.split('\n').filter(line => line.trim().startsWith('-')).length : 0;
                            const legacyRules = guideline.content && !guideline.keepSection ? guideline.content.split('\n').filter(line => line.trim().startsWith('-')).length : 0;
                            return keepRules + removeRules + legacyRules;
                          })()} rules
                        </span>
                        <span className="ai-guideline-separator">•</span>
                        <span className="ai-guideline-date">
                          Created {new Date(guideline.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="ai-cleaner-buttons">
                <button 
                  className="ai-cleaner-cancel-btn" 
                  onClick={closeAiCleaner}
                >
                  Cancel
                </button>
                <button 
                  className="ai-cleaner-start-btn"
                  onClick={handleStartWithSelectedGuideline}
                  disabled={!selectedGuideline}
                >
                  <img src="/img/wand.svg" alt="Start Cleaning" />
                  Start Cleaning
                </button>
              </div>
            </div>
          )}
          
          {step === 'processing' && (
            <div className="ai-cleaner-processing">
              <div className="ai-processing-grid"></div>
              
              {/* Runder Icon-Container */}
              <div className="ai-cleaner-icon-circle">
                <img src="/img/salesgod_dark.webp" alt="AI Cleaning" 
                  className="rotating-icon"
                  onError={(e) => {
                    // Fallback wenn das Bild nicht gefunden wird
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjEyIiBjeT0iMTIiIHI9IjEwIj48L2NpcmNsZT48cGF0aCBkPSJNMTYgMTJsLTQgNGw0IDQiPjwvcGF0aD48cGF0aCBkPSJNOCAxMmw0IDRsLTQgNCI+PC9wYXRoPjwvc3ZnPg==";
                  }}
                />
              </div>
              
              {/* Kontakt-Zähler */}
              <div className="ai-cleaner-counter">
                <span className="current">{Math.round(progress * bulkContactsData.length / 100)}</span>
                <span className="separator">of</span>
                <span className="total">{bulkContactsData.length}</span>
                <span>contacts analyzed</span>
              </div>
              
              <h3>Analyzing contacts...</h3>
              <div className="ai-cleaner-progress-container">
                <div 
                  className="ai-cleaner-progress-bar" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p>{getProgressText(progress)}</p>
            </div>
          )}
          
          {step === 'emailValidation' && (
            <div className="ai-cleaner-email-validation">
              <div className="ai-email-validation-grid"></div>
              
              {/* Runder Icon-Container für E-Mail-Validierung */}
              <div className="ai-cleaner-icon-circle email-validation">
                <img src="/img/salesgod_dark.webp" alt="Email Validation" 
                  className="rotating-icon"
                  onError={(e) => {
                    // Fallback wenn das Bild nicht gefunden wird
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjEyIiBjeT0iMTIiIHI9IjEwIj48L2NpcmNsZT48cGF0aCBkPSJNMTYgMTJsLTQgNGw0IDQiPjwvcGF0aD48cGF0aCBkPSJNOCAxMmw0IDRsLTQgNCI+PC9wYXRoPjwvc3ZnPg==";
                  }}
                />
              </div>
              
              {/* E-Mail-Validierungs-Zähler */}
              <div className="ai-cleaner-counter">
                <span className="current">{emailValidationResults.totalProcessed}</span>
                <span className="separator">of</span>
                <span className="total">{
                  [...processedData.toKeep, ...processedData.toRename, ...processedData.toRemove]
                    .filter(contact => selectedToKeep[contact.id] && contact.lead_email).length
                }</span>
                <span>emails validated</span>
              </div>
              
              <h3>Validating email addresses...</h3>
              <div className="ai-cleaner-progress-container">
                <div 
                  className="ai-cleaner-progress-bar email-validation" 
                  style={{ width: `${emailValidationProgress}%` }}
                ></div>
              </div>
              <p>{getEmailValidationText()}</p>
              
              {/* Zeige Zwischenergebnisse */}
     
            </div>
          )}

          {step === 'crmCheck' && (
            <div className="ai-cleaner-email-validation">
              <div className="ai-email-validation-grid"></div>

              <div className="ai-cleaner-icon-circle email-validation">
                <img
                  src="/img/salesgod_dark.webp"
                  alt="CRM Domain Check"
                  className="rotating-icon"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjEyIiBjeT0iMTIiIHI9IjEwIj48L2NpcmNsZT48L3N2Zz4=";
                  }}
                />
              </div>

              <div className="ai-cleaner-counter">
                <span className="current">{crmCheckResults.totalDomains}</span>
                <span className="separator">unique</span>
                <span>domains against Close CRM</span>
              </div>

              <h3>Checking against Close CRM…</h3>
              <div className="ai-cleaner-progress-container">
                <div
                  className="ai-cleaner-progress-bar email-validation"
                  style={{ width: crmCheckResults.totalDomains > 0 ? '90%' : '0%' }}
                ></div>
              </div>
              <p>
                Cross-checking {crmCheckResults.totalDomains} domain{crmCheckResults.totalDomains === 1 ? '' : 's'} so
                we don't accidentally contact leads that already live in Close with a different Lead Source.
              </p>
            </div>
          )}

          {step === 'confirm' && (
            <div className="ai-cleaner-confirm">
              
              {creditWarning && (
                <div className="ai-cleaner-credit-warning">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>
                    {creditWarning.exhausted
                      ? `Not enough credits — cleaning stopped after ${creditWarning.processed} of ${creditWarning.total} contacts. Add more credits to process the rest.`
                      : `Low credits: ${creditWarning.available} available, ${creditWarning.total} needed. Only ${creditWarning.affordable} contacts can be cleaned.`}
                  </span>
                </div>
              )}

              <div className="ai-cleaner-summary">
                <div className="ai-cleaner-summary-item keep">
                  <div className="ai-cleaner-summary-icon">
                    <img src="/img/done.svg" alt="Keep Icon" />
                  </div>
                  <span className="ai-cleaner-summary-count">{processedData.toKeep.length + processedData.toRename.length}</span>
                  <span className="ai-cleaner-summary-label">Keep</span>
                  <span className="ai-cleaner-summary-description">{selectedGuideline?.emailValidationOnly ? 'Contacts with valid emails' : 'AI-verified with valid emails'}</span>
                </div>
                {processedData.toRename.length > 0 && (
                  <div className="ai-cleaner-summary-item rename">
                    <div className="ai-cleaner-summary-icon">
                      <img src="/img/wand-2.svg" alt="Rename Icon" />
                    </div>
                    <span className="ai-cleaner-summary-count">{processedData.toRename.length}</span>
                    <span className="ai-cleaner-summary-label">Rename</span>
                    <span className="ai-cleaner-summary-description">Business names cleaned up</span>
                  </div>
                )}
                <div className="ai-cleaner-summary-item remove">
                  <div className="ai-cleaner-summary-icon">
                    <img src="/img/danger.svg" alt="Remove Icon" />
                  </div>
                  <span className="ai-cleaner-summary-count">{processedData.toRemove.length}</span>
                  <span className="ai-cleaner-summary-label">Remove</span>
                  <span className="ai-cleaner-summary-description">
                    {(() => {
                      const crmBlockedCount = processedData.toRemove.filter((c) => c.crmBlocked).length;
                      if (selectedGuideline?.emailValidationOnly) {
                        return crmBlockedCount > 0
                          ? `Invalid emails + ${crmBlockedCount} blocked by Close CRM`
                          : 'Invalid or missing emails';
                      }
                      return crmBlockedCount > 0
                        ? `AI-rejected, invalid emails, + ${crmBlockedCount} blocked by Close CRM`
                        : 'AI-rejected or invalid emails';
                    })()}
                  </span>
                </div>
              </div>

              {/* DM-Email-Validierungsergebnisse */}
              {(dmEmailValidationResults.totalProcessed > 0 || dmEmailValidationResults.skippedCount > 0) && (
                <div className="ai-cleaner-dm-validation-summary">
                  <div className="ai-cleaner-dm-validation-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <div className="ai-cleaner-dm-validation-text">
                    <span className="ai-cleaner-dm-validation-title">Decision Maker Emails</span>
                    <span className="ai-cleaner-dm-validation-details">
                      {dmEmailValidationResults.totalProcessed > 0 && (
                        <>
                          <span className="dm-stat dm-valid">{dmEmailValidationResults.validCount} valid</span>
                          {dmEmailValidationResults.invalidCount > 0 && (
                            <span className="dm-stat dm-invalid">{dmEmailValidationResults.invalidCount} invalid removed</span>
                          )}
                        </>
                      )}
                      {dmEmailValidationResults.skippedCount > 0 && (
                        <span className="dm-stat dm-skipped">{dmEmailValidationResults.skippedCount} already verified (skipped)</span>
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Close-CRM-Domain-Check-Ergebnisse */}
              {crmCheckResults.totalDomains > 0 && (
                <div className="ai-cleaner-crm-validation-summary">
                  <div className="ai-cleaner-crm-validation-icon">
                    <i className="fa fa-shield-halved"></i>
                  </div>
                  <div className="ai-cleaner-crm-validation-text">
                    <span className="ai-cleaner-crm-validation-title">
                      Close CRM Domain Check
                      <span className="crm-check-subtitle">
                        {crmCheckResults.totalDomains} domain{crmCheckResults.totalDomains === 1 ? '' : 's'} checked
                      </span>
                    </span>
                    <span className="ai-cleaner-crm-validation-details">
                      {crmCheckResults.blockedContacts > 0 && (
                        <span className="crm-stat crm-blocked">
                          <i className="fa fa-ban"></i>
                          {crmCheckResults.blockedContacts} blocked (already in Close)
                        </span>
                      )}
                      {crmCheckResults.allowedContacts > 0 && (
                        <span className="crm-stat crm-allowed">
                          <i className="fa fa-check"></i>
                          {crmCheckResults.allowedContacts} allowed
                        </span>
                      )}
                      {crmCheckResults.errorDomains > 0 && (
                        <span className="crm-stat crm-error">
                          <i className="fa fa-triangle-exclamation"></i>
                          {crmCheckResults.errorDomains} check error{crmCheckResults.errorDomains === 1 ? '' : 's'} (kept as safe default)
                        </span>
                      )}
                      {crmCheckResults.blockedContacts === 0 && crmCheckResults.errorDomains === 0 && (
                        <span className="crm-stat crm-clean">
                          <i className="fa fa-circle-check"></i>
                          No conflicts — all domains cleared
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Behalten-Sektion */}
              {processedData.toKeep.length > 0 && (
                <div className="ai-cleaner-section">
                  <div className="ai-cleaner-section-header" onClick={() => setIsKeepSectionOpen(!isKeepSectionOpen)}>
                    <div className="ai-cleaner-section-title">
                      <h4>
                        {selectedGuideline?.emailValidationOnly ? 'Valid Emails' : 'Contacts to Keep'}
                        <span className="section-count keep">{processedData.toKeep.length}</span>
                      </h4>
                    </div>
                    {isKeepSectionOpen && (
                      <div className="ai-cleaner-select-all" onClick={(e) => e.stopPropagation()}>
                        <label>
                          <input 
                            type="checkbox" 
                            checked={processedData.toKeep.every(contact => selectedToKeep[contact.id])}
                            onChange={(e) => toggleAllInCategory('toKeep', e.target.checked)}
                          />
                          Select all
                        </label>
                      </div>
                    )}
                    <div className={`ai-cleaner-toggle-icon ${isKeepSectionOpen ? 'open' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                  
                  {isKeepSectionOpen && (
                    <div className="ai-cleaner-contacts-list">
                      {processedData.toKeep
                        .sort((a, b) => (a.confidence || 5) - (b.confidence || 5))
                        .map(contact => (
                        <div key={contact.id} className={`ai-cleaner-contact-item ${contact.analysisError ? 'analysis-error' : ''}`}>
                          <div className="ai-cleaner-contact-select">
                            <input 
                              type="checkbox" 
                              checked={!!selectedToKeep[contact.id]} 
                              onChange={() => toggleContactSelection(contact.id)}
                            />
                          </div>
                          <div className="ai-cleaner-contact-details">
                            <div className="ai-cleaner-contact-name">
                              {contact.business_name || "No name"}
                              {contact.analysisError && (
                                <span className="ai-cleaner-error-badge">Analysis failed</span>
                              )}
                              {!contact.analysisError && contact.confidence && (
                                <span className="ai-cleaner-confidence-badge">{contact.confidence}</span>
                              )}
                              {contact.pacing && !contact.analysisError && (
                                <span className={`pacing-badge ${contact.pacing}`}>{getPacingLabel(contact.pacing)}</span>
                              )}
                            </div>
                            <div className="ai-cleaner-contact-description">
                              {contact.lead_description || "No description"}
                            </div>
                            {contact.reason && (
                              <div className={`ai-cleaner-contact-reason ${contact.analysisError ? 'error-reason' : 'keep-reason'}`}>
                                {renderReasonBullets(contact.reason)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Umbenennen-Sektion */}
              {processedData.toRename.length > 0 && (
                <div className="ai-cleaner-section">
                  <div className="ai-cleaner-section-header" onClick={() => setIsRenameSectionOpen(!isRenameSectionOpen)}>
                    <div className="ai-cleaner-section-title">
                      <h4>
                        Contacts to Rename 
                        <span className="section-count rename">{processedData.toRename.length}</span>
                      </h4>
                    </div>
                    {isRenameSectionOpen && (
                      <div className="ai-cleaner-select-all" onClick={(e) => e.stopPropagation()}>
                        <label>
                          <input 
                            type="checkbox" 
                            checked={processedData.toRename.every(contact => selectedToKeep[contact.id])}
                            onChange={(e) => toggleAllInCategory('toRename', e.target.checked)}
                          />
                          Select all
                        </label>
                      </div>
                    )}
                    <div className={`ai-cleaner-toggle-icon ${isRenameSectionOpen ? 'open' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                  
                  {isRenameSectionOpen && (
                    <div className="ai-cleaner-contacts-list">
                      {processedData.toRename
                        .sort((a, b) => (a.confidence || 5) - (b.confidence || 5))
                        .map(contact => (
                        <div key={contact.id} className="ai-cleaner-contact-item">
                          <div className="ai-cleaner-contact-select">
                            <input 
                              type="checkbox" 
                              checked={!!selectedToKeep[contact.id]} 
                              onChange={() => toggleContactSelection(contact.id)}
                            />
                          </div>
                          <div className="ai-cleaner-contact-details">
                            <div className="ai-cleaner-contact-name">
                              <span className="ai-cleaner-original">{contact.original}</span>
                              <span className="ai-cleaner-arrow">→</span>
                              <span className="ai-cleaner-cleaned">{contact.cleaned}</span>
                              {contact.confidence && (
                                <span className="ai-cleaner-confidence-badge">{contact.confidence}</span>
                              )}
                              {contact.pacing && (
                                <span className={`pacing-badge ${contact.pacing}`}>{getPacingLabel(contact.pacing)}</span>
                              )}
                            </div>
                            <div className="ai-cleaner-contact-description">
                              {contact.lead_description || "No description"}
                            </div>
                            {contact.reason && (
                              <div className="ai-cleaner-contact-reason keep-reason">
                                {renderReasonBullets(contact.reason)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Entfernen-Sektion */}
              {processedData.toRemove.length > 0 && (
                <div className="ai-cleaner-section">
                  <div className="ai-cleaner-section-header" onClick={() => setIsRemoveSectionOpen(!isRemoveSectionOpen)}>
                    <div className="ai-cleaner-section-title">
                      <h4>
                        {selectedGuideline?.emailValidationOnly ? 'Invalid Emails' : 'Contacts to Remove'}
                        <span className="section-count remove">{processedData.toRemove.length}</span>
                      </h4>
                    </div>
                    {isRemoveSectionOpen && (
                      <div className="ai-cleaner-select-all" onClick={(e) => e.stopPropagation()}>
                        <label>
                          <input 
                            type="checkbox" 
                            checked={processedData.toRemove.every(contact => selectedToKeep[contact.id])}
                            onChange={(e) => toggleAllInCategory('toRemove', e.target.checked)}
                          />
                          Keep all
                        </label>
                      </div>
                    )}
                    <div className={`ai-cleaner-toggle-icon ${isRemoveSectionOpen ? 'open' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                  
                  {isRemoveSectionOpen && (
                    <>
                      {/* Separiere AI-rejected, Email-invalid, und CRM-geblockt */}
                      {processedData.toRemove.filter(contact => !contact.emailValidationFailed && !contact.crmBlocked).length > 0 && (
                        <div className="ai-cleaner-subsection">
                          <h5 className="ai-cleaner-subsection-title">
                            <span className="ai-rejection-icon"><img src="/img/stamp.svg" alt="AI Rejected" style={{width: '20px', height: '20px'}}/></span>
                            AI-Rejected
                          </h5>
                          <div className="ai-cleaner-contacts-list">
                            {processedData.toRemove
                              .filter(contact => !contact.emailValidationFailed && !contact.crmBlocked)
                              .sort((a, b) => (a.confidence || 5) - (b.confidence || 5))
                              .map(contact => {
                                const isKept = !!selectedToKeep[contact.id];
                                return (
                                <div key={contact.id} className={`ai-cleaner-contact-item ai-rejected${isKept ? ' keeping' : ''}`}>
                                  <div className="ai-cleaner-contact-select">
                                    <input 
                                      type="checkbox" 
                                      checked={isKept} 
                                      onChange={() => toggleContactSelection(contact.id)}
                                    />
                                  </div>
                                  <div className="ai-cleaner-contact-details">
                                    <div className="ai-cleaner-contact-name">
                                      {contact.business_name || "No name"}
                                      {isKept && <span className="keeping-badge">Keeping</span>}
                                    </div>
                                    <div className="ai-cleaner-contact-description">
                                      {contact.lead_description || "No description"}
                                    </div>
                                    <div className="ai-cleaner-contact-pro-contra-wrapper">
                                      {renderProContra(contact)}
                                    </div>
                                  </div>
                                </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                      
                      {/* Blocked by Close CRM */}
                      {processedData.toRemove.filter(contact => contact.crmBlocked).length > 0 && (
                        <div className="ai-cleaner-subsection">
                          <h5 className="ai-cleaner-subsection-title">
                            <span className="email-invalid-icon" style={{ background: '#e8f7f1', color: '#0c8b68' }}>
                              <i className="fa fa-shield-halved" style={{ fontSize: 14 }}></i>
                            </span>
                            Blocked by Close CRM ({processedData.toRemove.filter(c => c.crmBlocked).length})
                          </h5>
                          <div className="ai-cleaner-contacts-list">
                            {processedData.toRemove
                              .filter(contact => contact.crmBlocked)
                              .sort((a, b) => (a.business_name || '').localeCompare(b.business_name || ''))
                              .map(contact => {
                                const isKept = !!selectedToKeep[contact.id];
                                return (
                                  <div key={contact.id} className={`ai-cleaner-contact-item email-invalid${isKept ? ' keeping' : ''}`}>
                                    <div className="ai-cleaner-contact-select">
                                      <input
                                        type="checkbox"
                                        checked={isKept}
                                        onChange={() => toggleContactSelection(contact.id)}
                                      />
                                    </div>
                                    <div className="ai-cleaner-contact-details">
                                      <div className="ai-cleaner-contact-name">
                                        {contact.business_name || 'No name'}
                                        <span className="ai-cleaner-email-address">{contact.lead_email}</span>
                                        {isKept && <span className="keeping-badge">Keeping</span>}
                                      </div>
                                      <div className="ai-cleaner-contact-description">
                                        {contact.lead_description || 'No description'}
                                      </div>
                                      <div className={`ai-cleaner-contact-reason ai-cleaner-email-reason${isKept ? ' keep-reason' : ''}`}>
                                        {renderReasonBullets(contact.reason)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      {/* E-Mail-invalid Kontakte */}
                      {processedData.toRemove.filter(contact => contact.emailValidationFailed).length > 0 && (
                        <div className="ai-cleaner-subsection">
                          <h5 className="ai-cleaner-subsection-title">
                            <span className="email-invalid-icon"><img src="/img/stamp.svg" alt="Invalid Email" style={{width: '20px', height: '20px'}}/></span>
                            Invalid Email
                          </h5>
                          <div className="ai-cleaner-contacts-list">
                            {processedData.toRemove
                              .filter(contact => contact.emailValidationFailed)
                              .sort((a, b) => (a.confidence || 5) - (b.confidence || 5))
                              .map(contact => {
                                const isKept = !!selectedToKeep[contact.id];
                                return (
                                <div key={contact.id} className={`ai-cleaner-contact-item email-invalid${isKept ? ' keeping' : ''}`}>
                                  <div className="ai-cleaner-contact-select">
                                    <input 
                                      type="checkbox" 
                                      checked={isKept} 
                                      onChange={() => toggleContactSelection(contact.id)}
                                    />
                                  </div>
                                  <div className="ai-cleaner-contact-details">
                                    <div className="ai-cleaner-contact-name">
                                      {contact.business_name || "No name"}
                                      <span className="ai-cleaner-email-address">{contact.lead_email}</span>
                                      {isKept && <span className="keeping-badge">Keeping</span>}
                                    </div>
                                    <div className="ai-cleaner-contact-description">
                                      {contact.lead_description || "No description"}
                                    </div>
                                    <div className={`ai-cleaner-contact-reason ai-cleaner-email-reason${isKept ? ' keep-reason' : ''}`}>
                                      {renderReasonBullets(contact.reason)}
                                    </div>
                                  </div>
                                </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {processedData.toRemove.length === 0 && processedData.toKeep.length > 0 && (
                <div className="ai-cleaner-all-valid-banner">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <span>All emails are valid — no contacts need to be removed</span>
                </div>
              )}
              
              <div className="ai-cleaner-actions">
                <div className="ai-cleaner-next-action">
                  <p>Next step:</p>
                  <div className="ai-cleaner-next-options">
                    <label>
                      <input 
                        type="radio" 
                        name="nextAction" 
                        checked={!openAiManagerAfter && !openAutoSendAfter && !assignFlowAfter} 
                        onChange={() => {
                          setOpenAiManagerAfter(false);
                          setOpenAutoSendAfter(false);
                          setAssignFlowAfter(false);
                        }}
                      />
                      Update List
                    </label>
                    <label>
                      <input 
                        type="radio" 
                        name="nextAction" 
                        checked={openAiManagerAfter} 
                        onChange={() => {
                          setOpenAiManagerAfter(true);
                          setOpenAutoSendAfter(false);
                          setAssignFlowAfter(false);
                        }}
                      />
                      Open AI Manager
                    </label>
                    {showAutoSendButton && (
                      <label>
                        <input 
                          type="radio" 
                          name="nextAction" 
                          checked={openAutoSendAfter} 
                          onChange={() => {
                            setOpenAiManagerAfter(false);
                            setOpenAutoSendAfter(true);
                            setAssignFlowAfter(false);
                          }}
                        />
                        Open AutoSend
                      </label>
                    )}
                    {warmupFlows.length > 0 && (
                      <label>
                        <input 
                          type="radio" 
                          name="nextAction" 
                          checked={assignFlowAfter} 
                          onChange={() => {
                            setOpenAiManagerAfter(false);
                            setOpenAutoSendAfter(false);
                            setAssignFlowAfter(true);
                          }}
                        />
                        Assign Flow
                      </label>
                    )}
                  </div>

                  {assignFlowAfter && (
                    <div className="ai-cleaner-flow-select">
                      <div className="ai-cleaner-flow-select-header">
                        <div className="ai-cleaner-flow-select-icon">
                          <i className="fa fa-route"></i>
                        </div>
                        <div className="ai-cleaner-flow-select-title">
                          <span className="ai-cleaner-flow-select-heading">Assign Warmup Flow</span>
                          <span className="ai-cleaner-flow-select-subtitle">
                            {(() => {
                              const keepCount = processedData.toKeep.filter(c => selectedToKeep[c.id]).length 
                                + processedData.toRename.filter(c => selectedToKeep[c.id]).length;
                              return `${keepCount} contacts will be assigned`;
                            })()}
                          </span>
                        </div>
                      </div>
                      <div className="ai-cleaner-flow-list">
                        {warmupFlows.map(flow => (
                          <div 
                            key={flow.id}
                            className={`ai-cleaner-flow-option ${selectedFlowId === flow.id ? 'selected' : ''}`}
                            onClick={() => setSelectedFlowId(flow.id)}
                          >
                            <div className={`ai-cleaner-flow-option-icon ${selectedFlowId === flow.id ? 'active' : ''}`}>
                              <i className="fa fa-play"></i>
                            </div>
                            <div className="ai-cleaner-flow-info">
                              <span className="ai-cleaner-flow-name">{flow.name}</span>
                              <span className="ai-cleaner-flow-meta">{flow.steps?.length || 0} steps</span>
                            </div>
                            <div className={`ai-cleaner-flow-check ${selectedFlowId === flow.id ? 'visible' : ''}`}>
                              <i className="fa fa-check"></i>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="ai-cleaner-buttons">
                  <button 
                    className="ai-cleaner-cancel-btn" 
                    onClick={closeAiCleaner}
                    disabled={isConfirming}
                  >
                    Cancel
                  </button>
                  <button 
                    className={`ai-cleaner-confirm-btn ${isConfirming ? 'loading' : ''}`}
                    onClick={confirmChanges}
                    disabled={isConfirming || (assignFlowAfter && !selectedFlowId)}
                  >
                    {isConfirming && <span className="confirm-spinner"></span>}
                    {isConfirming ? 'Applying changes...' : 'Confirm Changes'}
                    {!isConfirming && (
                      <img 
                        src="/img/done.svg" 
                        alt="Confirm" 
                        className="confirm-icon"
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiCleanerPopup; 